class HotspotProductSection extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.safeId = this.sectionId.replace(/-/g, '_');
    this.enableModal = this.dataset.enableModal === 'true';
    this.initializeEventListeners();
  }

  connectedCallback() {
    console.log('HotspotProductSection connected');
  }

  initializeEventListeners() {
    // Initialize modal functionality if enabled
    if (this.enableModal) {
      this.initializeModalFunctionality();
    }
    
    // Initialize direct add to cart functionality
    this.initializeDirectAddToCart();
    
    // Initialize quantity selectors
    this.initializeQuantitySelectors();
    
    // Initialize variant selectors
    this.initializeVariantSelectors();
    
    // Initialize slider functionality
    this.initializeSliders();
  }

  initializeModalFunctionality() {
    // Open modal
    window[`openProductPopup${this.safeId}`] = (blockId) => {
      const popup = document.getElementById(`popup-${this.sectionId}-${blockId}`);
      const overlay = document.querySelector(`.product-popup-overlay-${this.sectionId}`);
      
      if (popup && overlay) {
        this.initializeSlider(this.sectionId, blockId);
        overlay.classList.add('active');
        popup.classList.add('active');
        document.body.style.overflow = 'hidden';
      }
    };

    // Close modal
    window[`closeProductPopup${this.safeId}`] = () => {
      const popups = document.querySelectorAll(`.product-popup-${this.sectionId}`);
      const overlay = document.querySelector(`.product-popup-overlay-${this.sectionId}`);
      
      popups.forEach(popup => popup.classList.remove('active'));
      if (overlay) overlay.classList.remove('active');
      document.body.style.overflow = '';
    };

    // Add to cart from modal
    window[`addToCartPopup${this.safeId}`] = async (blockId) => {
      const button = document.getElementById(`add-to-cart-${this.sectionId}-${blockId}`);
      const quantityInput = document.getElementById(`quantity-${this.sectionId}-${blockId}`);
      const variantInput = document.getElementById(`variant-id-${this.sectionId}-${blockId}`);
      const successMessage = document.getElementById(`success-message-${this.sectionId}-${blockId}`);
      
      const variantId = variantInput.value;
      const quantity = quantityInput ? parseInt(quantityInput.value) : 1;

      button.disabled = true;
      button.classList.add('adding');

      try {
        // Add to cart
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: variantId,
            quantity: quantity
          })
        });

        if (!response.ok) {
          throw new Error('Failed to add to cart');
        }

        const data = await response.json();
        console.log('Item added to cart:', data);
        
        // Show success animation
        button.classList.remove('adding');
        button.classList.add('added');
        
        if (successMessage) {
          successMessage.classList.add('show');
        }

        // Update cart - THIS IS THE KEY
        await this.refreshCart();
        
        // Reset button state
        setTimeout(() => {
          button.classList.remove('added');
          button.disabled = false;
          if (successMessage) {
            successMessage.classList.remove('show');
          }
        }, 2000);
        
        // Close popup after success
        setTimeout(() => {
          window[`closeProductPopup${this.safeId}`]();
        }, 1500);
        
      } catch (error) {
        console.error('Error adding to cart:', error);
        button.classList.remove('adding');
        button.disabled = false;
        if (successMessage) {
          successMessage.textContent = 'Error adding to cart';
          successMessage.style.background = '#ff4444';
          successMessage.classList.add('show');
          setTimeout(() => {
            successMessage.classList.remove('show');
            successMessage.textContent = '✓ Added to cart successfully!';
            successMessage.style.background = '#4CAF50';
          }, 3000);
        }
      }
    };

    // Close on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.enableModal) {
        window[`closeProductPopup${this.safeId}`]();
      }
    });
  }

  initializeDirectAddToCart() {
    // Direct add to cart (when modal is disabled)
    window[`addToCartDirect${this.safeId}`] = async (blockId) => {
      const hotspot = document.querySelector(`.hotspot-${this.sectionId}[data-block-id="${blockId}"]`);
      if (!hotspot) return;

      const variantId = hotspot.dataset.variantId;
      const quantity = 1;
      const originalContent = hotspot.innerHTML;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: variantId,
            quantity: quantity
          })
        });

        if (!response.ok) {
          throw new Error('Failed to add to cart');
        }

        const data = await response.json();
        console.log('Item added to cart:', data);
        
        // Show temporary success indicator
        hotspot.innerHTML = '✓';
        hotspot.style.background = '#4CAF50';
        hotspot.style.color = 'white';
        
        // Update cart
        await this.refreshCart();
        
        setTimeout(() => {
          hotspot.innerHTML = originalContent;
          hotspot.style.background = '';
          hotspot.style.color = '';
        }, 1000);
        
      } catch (error) {
        console.error('Error adding to cart:', error);
        hotspot.innerHTML = '!';
        hotspot.style.background = '#ff4444';
        setTimeout(() => {
          hotspot.innerHTML = originalContent;
          hotspot.style.background = '';
        }, 1000);
      }
    };
  }

  initializeQuantitySelectors() {
    // Quantity change in modal
    window[`changeQuantity${this.safeId}`] = (blockId, change) => {
      const input = document.getElementById(`quantity-${this.sectionId}-${blockId}`);
      if (input) {
        const currentValue = parseInt(input.value) || 1;
        const newValue = Math.max(1, currentValue + change);
        input.value = newValue;
      }
    };
  }

  initializeVariantSelectors() {
    // Variant selection in modal (buttons/swatches)
    window[`selectOption${this.safeId}`] = (blockId, optionName, optionValue) => {
      const picker = document.querySelector(`#popup-${this.sectionId}-${blockId} .popup-variant-picker-${this.sectionId}`);
      if (!picker) return;
      
      const optionGroup = event.target.closest('fieldset');
      
      // Remove selected class from all options in this group
      if (optionGroup.classList.contains(`variant-option-swatches-${this.sectionId}`)) {
        optionGroup.querySelectorAll(`.swatch-${this.sectionId}`).forEach(swatch => {
          swatch.classList.remove('selected');
        });
        event.target.classList.add('selected');
      } else {
        optionGroup.querySelectorAll(`.variant-option-button-label-${this.sectionId}`).forEach(label => {
          label.classList.remove('selected');
        });
        event.target.closest(`.variant-option-button-label-${this.sectionId}`).classList.add('selected');
      }
      
      this.updateSelectedVariant(this.sectionId, blockId);
    };

    // Variant selection in modal (dropdowns)
    window[`selectOptionDropdown${this.safeId}`] = (blockId, optionName, optionValue) => {
      this.updateSelectedVariant(this.sectionId, blockId);
    };
  }

  updateSelectedVariant(sectionId, blockId) {
    const picker = document.querySelector(`#popup-${sectionId}-${blockId} .popup-variant-picker-${sectionId}`);
    if (!picker) return;

    const selectedOptions = [];
    picker.querySelectorAll('fieldset').forEach(fieldset => {
      if (fieldset.classList.contains(`variant-option-swatches-${sectionId}`)) {
        const selectedSwatch = fieldset.querySelector(`.swatch-${sectionId}.selected`);
        if (selectedSwatch) {
          selectedOptions.push(selectedSwatch.dataset.optionValue);
        }
      } else if (fieldset.classList.contains(`variant-option-dropdowns-${sectionId}`)) {
        const select = fieldset.querySelector(`.variant-option-select-${sectionId}`);
        if (select) {
          selectedOptions.push(select.value);
        }
      } else {
        const selectedLabel = fieldset.querySelector(`.variant-option-button-label-${sectionId}.selected`);
        if (selectedLabel) {
          const input = selectedLabel.querySelector('input');
          if (input) selectedOptions.push(input.value);
        }
      }
    });

    // Update variant ID - in a real implementation, you would find the matching variant
    const variantInput = document.getElementById(`variant-id-${sectionId}-${blockId}`);
    // Keep current variant ID for now
  }

  initializeSliders() {
    // Initialize slider for a specific popup
    this.initializeSlider = (sectionId, blockId) => {
      const container = document.getElementById(`slider-container-${sectionId}-${blockId}`);
      if (!container) return;

      const track = container.querySelector(`.popup-slider-track-${sectionId}`);
      const slides = container.querySelectorAll(`.popup-slide-${sectionId}`);
      const dots = container.querySelectorAll(`.popup-slider-dot-${sectionId}`);
      const totalSlides = slides.length;
      let currentSlide = 0;

      window[`slideTo${this.safeId}`] = (blockId, index) => {
        if (blockId !== blockId) return;
        currentSlide = index;
        this.updateSlider(track, dots, currentSlide);
      };

      window[`slidePrev${this.safeId}`] = (blockId) => {
        if (blockId !== blockId) return;
        currentSlide = (currentSlide - 1 + totalSlides) % totalSlides;
        this.updateSlider(track, dots, currentSlide);
      };

      window[`slideNext${this.safeId}`] = (blockId) => {
        if (blockId !== blockId) return;
        currentSlide = (currentSlide + 1) % totalSlides;
        this.updateSlider(track, dots, currentSlide);
      };

      // Initialize slider
      this.updateSlider(track, dots, currentSlide);
    };

    // Update slider position
    this.updateSlider = (track, dots, currentSlide) => {
      if (track) {
        track.style.transform = `translateX(-${currentSlide * 100}%)`;
      }
      if (dots && dots.length > 0) {
        dots.forEach((dot, index) => {
          dot.classList.toggle('active', index === currentSlide);
        });
      }
    };
  }

  // Main refresh cart function
  async refreshCart() {
    console.log('Starting cart refresh...');
    
    try {
      // Step 1: Get fresh cart data
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) {
        throw new Error('Failed to fetch cart data');
      }
      const cartData = await cartResponse.json();
      console.log('Cart data fetched:', cartData);
      
      // Step 2: Update cart bubbles
      this.updateCartBubbles(cartData);
      
      // Step 3: Find the cart-items-component's section ID
      const cartItemsComponent = document.querySelector('cart-items-component');
      if (!cartItemsComponent) {
        console.error('cart-items-component not found');
        return;
      }
      
      const sectionId = cartItemsComponent.getAttribute('data-section-id');
      console.log('Cart drawer section ID:', sectionId);
      
      // Step 4: Fetch fresh cart drawer HTML using the correct section ID
      const timestamp = Date.now();
      const sectionsUrl = `/?section_id=${sectionId}&t=${timestamp}`;
      console.log('Fetching from:', sectionsUrl);
      
      const sectionsResponse = await fetch(sectionsUrl);
      
      if (sectionsResponse.ok) {
        const html = await sectionsResponse.text();
        console.log('Section HTML fetched, length:', html.length);
        
        this.updateCartDrawerContent(html);
      } else {
        console.error('Failed to fetch section:', sectionsResponse.status);
      }
      
      // Step 5: Open the cart drawer
      setTimeout(() => {
        this.openCartDrawer();
      }, 300);
      
      console.log('Cart refresh complete');
      
    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
  }

  // Update cart bubbles
  updateCartBubbles(cartData) {
    console.log('Updating cart bubbles with count:', cartData.item_count);
    
    // Find ALL cart bubble elements on the page
    const cartBubbles = document.querySelectorAll('.cart-bubble');
    const cartBubbleCounts = document.querySelectorAll('.cart-bubble__text-count');
    const cartBubbleTexts = document.querySelectorAll('#cart-bubble-text .visually-hidden');
    
    console.log('Found bubbles:', cartBubbles.length);
    console.log('Found count elements:', cartBubbleCounts.length);
    
    // Update each cart bubble
    cartBubbles.forEach(cartBubble => {
      if (cartData.item_count > 0) {
        cartBubble.classList.remove('visually-hidden');
      } else {
        cartBubble.classList.add('visually-hidden');
      }
      
      // Update data-maintain-ratio attribute
      if (cartData.item_count <= 99) {
        cartBubble.setAttribute('data-maintain-ratio', '');
      } else {
        cartBubble.removeAttribute('data-maintain-ratio');
      }
    });
    
    // Update all count elements
    cartBubbleCounts.forEach(cartBubbleCount => {
      cartBubbleCount.textContent = cartData.item_count;
      if (cartData.item_count > 0) {
        cartBubbleCount.classList.remove('hidden');
      } else {
        cartBubbleCount.classList.add('hidden');
      }
    });
    
    // Update all aria labels
    cartBubbleTexts.forEach(cartBubbleText => {
      cartBubbleText.textContent = `${cartData.item_count}`;
    });
  }

  // Update cart drawer content
  updateCartDrawerContent(html) {
    console.log('Updating cart drawer content...');
    
    try {
      // Parse the HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Find the new cart items component
      const newCartItemsComponent = doc.querySelector('cart-items-component');
      
      if (!newCartItemsComponent) {
        console.error('cart-items-component not found in fetched HTML');
        return;
      }
      
      console.log('New cart items component found');
      
      // Find existing cart items component
      const existingCartItemsComponent = document.querySelector('cart-drawer-component cart-items-component');
      
      if (!existingCartItemsComponent) {
        console.error('Existing cart-items-component not found in DOM');
        return;
      }
      
      console.log('Existing cart items component found');
      
      // Preserve the existing class list and attributes
      const existingClasses = existingCartItemsComponent.className;
      const existingDataSectionId = existingCartItemsComponent.getAttribute('data-section-id');
      
      // Replace only the innerHTML to preserve the component's styles and attributes
      existingCartItemsComponent.innerHTML = newCartItemsComponent.innerHTML;
      
      // Restore original classes and attributes (in case they were changed)
      existingCartItemsComponent.className = existingClasses;
      if (existingDataSectionId) {
        existingCartItemsComponent.setAttribute('data-section-id', existingDataSectionId);
      }
      
      // Remove cart-drawer--empty class from dialog if it exists
      const cartDialog = document.querySelector('.cart-drawer__dialog');
      if (cartDialog && cartDialog.classList.contains('cart-drawer--empty')) {
        console.log('Removing cart-drawer--empty class from dialog');
        cartDialog.classList.remove('cart-drawer--empty');
      }
      
      // Update aria-labelledby to point to the correct heading
      if (cartDialog) {
        const currentLabel = cartDialog.getAttribute('aria-labelledby');
        if (currentLabel === 'cart-drawer-heading-empty') {
          console.log('Updating aria-labelledby to cart-drawer-heading');
          cartDialog.setAttribute('aria-labelledby', 'cart-drawer-heading');
        }
      }
      
      console.log('Cart drawer content updated successfully');
      
      // Re-initialize cart scripts if needed
      this.reinitializeCartScripts();
      
    } catch (error) {
      console.error('Error updating cart drawer content:', error);
    }
  }
  
  // Re-initialize cart scripts
  reinitializeCartScripts() {
    console.log('Re-initializing cart scripts...');
    
    // Dispatch an event to tell cart components to reinitialize
    document.dispatchEvent(new CustomEvent('cart:items-changed'));
    
    // Find any script tags in the cart items component and execute them
    const cartItemsComponent = document.querySelector('cart-drawer-component cart-items-component');
    if (cartItemsComponent) {
      const scripts = cartItemsComponent.querySelectorAll('script');
      scripts.forEach(script => {
        if (script.src) {
          // External script - reload it
          const newScript = document.createElement('script');
          newScript.src = script.src;
          if (script.type) newScript.type = script.type;
          if (script.getAttribute('fetchpriority')) {
            newScript.setAttribute('fetchpriority', script.getAttribute('fetchpriority'));
          }
          script.parentNode.replaceChild(newScript, script);
        } else if (script.textContent) {
          // Inline script - execute it
          const newScript = document.createElement('script');
          newScript.textContent = script.textContent;
          script.parentNode.replaceChild(newScript, script);
        }
      });
    }
    
    console.log('Cart scripts re-initialized');
  }

  // Open the cart drawer
  openCartDrawer() {
    console.log('Attempting to open cart drawer...');
    
    const cartDrawer = document.querySelector('cart-drawer-component');
    
    if (!cartDrawer) {
      console.error('cart-drawer-component not found');
      return;
    }
    
    // Check if auto-open is enabled
    const autoOpen = cartDrawer.hasAttribute('auto-open');
    console.log('Auto-open enabled:', autoOpen);
    
    if (autoOpen) {
      // Method 1: Try using the component's open method
      if (typeof cartDrawer.open === 'function') {
        console.log('Opening via component.open()');
        cartDrawer.open();
        return;
      }
      
      // Method 2: Try to open the dialog directly
      const dialog = cartDrawer.querySelector('dialog[ref="dialog"], .cart-drawer__dialog');
      if (dialog) {
        console.log('Found dialog, attempting to open');
        if (typeof dialog.showModal === 'function') {
          dialog.showModal();
          return;
        }
        if (!dialog.hasAttribute('open')) {
          dialog.setAttribute('open', '');
        }
        return;
      }
      
      // Method 3: Click the cart trigger button
      const cartTrigger = document.querySelector('[data-testid="cart-drawer-trigger"]');
      if (cartTrigger) {
        console.log('Clicking cart trigger');
        cartTrigger.click();
        return;
      }
    }
    
    console.log('Cart drawer auto-open not enabled or no method worked');
  }
}

// Register the custom element
if (!customElements.get('hotspot-product-section')) {
  customElements.define('hotspot-product-section', HotspotProductSection);
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  const sections = document.querySelectorAll('hotspot-product-section');
  sections.forEach(section => {
    console.log('Hotspot section found:', section.dataset.sectionId);
  });
});